import { Link } from 'react-router-dom';
import { AlertTriangle, Info, Rocket, X, ExternalLink } from 'lucide-react';
import type { AusrollBefund } from '../api';

/**
 * Was zwischen „Deploy" und dem Ausrollen steht, wenn es etwas zu sagen gibt.
 *
 * ══ WANN ES ERSCHEINT ══════════════════════════════════════════════════════
 *
 * Nur dann. Ist der Befund `sauber`, rollt der Knopf aus wie zuvor und dieses
 * Panel kommt nie zu Gesicht. Ein Zwischenschritt, der auch im Normalfall
 * erscheint, wird nach der dritten Wiederholung weggeklickt — und dann auch
 * das eine Mal, an dem er etwas zu sagen hatte.
 *
 * ══ WARUM ES AUSROLLEN TROTZDEM ANBIETET ═══════════════════════════════════
 *
 * Auch bei einem haltenden Befund. Der PC kennt den Fall nicht besser als der
 * Mensch davor: Ein Tag, den die Registry nicht führt, kann eine Minute später
 * da sein, und eine Vorlage bewusst nicht zu wechseln ist eine gültige
 * Entscheidung. Gesperrt wird nichts — nur die Beschriftung sagt, worauf man
 * sich einlässt.
 */

interface Props {
  befund: AusrollBefund;
  produktId: string;
  /** Läuft gerade eine Vorlagenumstellung? Dann wartet das Ausrollen. */
  umstellenLaeuft: boolean;
  onVorlageWaehlen: (templateId: string) => void;
  onAusrollen: () => void;
  onSchliessen: () => void;
}

export function AusrollPanel({
  befund,
  produktId,
  umstellenLaeuft,
  onVorlageWaehlen,
  onAusrollen,
  onSchliessen,
}: Props) {
  const haltend = befund.images.filter((b) => b.schwere === 'haltend');
  const hinweise = befund.images.filter((b) => b.schwere === 'hinweis');

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:p-8">
      <div className="w-full max-w-2xl rounded-xl bg-white shadow-xl dark:bg-slate-900">
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4 dark:border-slate-700">
          <div>
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              Vor dem Ausrollen
            </h2>
            <p className="mt-0.5 text-sm text-slate-500">
              {haltend.length > 0
                ? 'Etwas davon lässt den Deploy scheitern.'
                : 'Nichts, was den Deploy aufhält — aber einen Blick wert.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onSchliessen}
            className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800"
            aria-label="Schließen"
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          {/* ── Vorlage ───────────────────────────────────────────────────── */}
          {befund.vorlage.fehlt && (
            <Kasten schwere="haltend">
              <p className="font-medium">Dieser Instanz ist keine Vorlage zugeordnet.</p>
              <p className="mt-1">
                Ohne Vorlage gibt es keine compose.yaml und keine <code>.env</code> — der Deploy
                hat nichts auszurollen.
              </p>
              <ProduktLink produktId={produktId} text="Vorlagen dieses Produkts ansehen" />
            </Kasten>
          )}

          {befund.vorlage.veraltet && (
            <Kasten schwere="hinweis">
              <p className="font-medium">
                Es gibt eine neuere Vorlage: {befund.vorlage.neuereVersion}
              </p>
              <p className="mt-1">
                Diese Instanz steht auf {befund.vorlage.aktuellVersion ?? 'einer älteren Fassung'}.
                Eine neue Vorlage kann neue Pflichtwerte mitbringen — die legt der Deploy an,
                nicht das Umstellen.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  disabled={umstellenLaeuft}
                  onClick={() => onVorlageWaehlen(befund.vorlage.neuereId!)}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50 dark:bg-slate-100 dark:text-slate-900"
                >
                  {umstellenLaeuft
                    ? 'Wird umgestellt …'
                    : `Auf ${befund.vorlage.neuereVersion} umstellen`}
                </button>
                <ProduktLink produktId={produktId} text="Andere Vorlage wählen oder neue anlegen" />
              </div>
            </Kasten>
          )}

          {/* ── Images ────────────────────────────────────────────────────── */}
          {[...haltend, ...hinweise].map((b) => (
            <Kasten key={`${b.art}-${b.schluessel}`} schwere={b.schwere}>
              <p className="font-medium">{b.dienst}</p>
              <p className="mt-1">{b.meldung}</p>
              <p className="mt-1 text-xs text-slate-500">
                Zu ändern im Reiter „Bildtags", Feld <code>{b.schluessel}</code>.
              </p>
            </Kasten>
          ))}

          {/* ── Was nicht geprüft werden konnte ───────────────────────────── */}
          {befund.ungeprueft.length > 0 && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-400">
              <p className="font-medium">Nicht geprüft:</p>
              <ul className="mt-1 list-disc pl-4">
                {befund.ungeprueft.map((u) => (
                  <li key={u}>{u}</li>
                ))}
              </ul>
              <p className="mt-1">
                Die Registry war nicht erreichbar. Ob die Bildtags dort liegen, ist damit offen —
                nicht in Ordnung, sondern ungeklärt.
              </p>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-200 px-5 py-3 dark:border-slate-700">
          <button
            type="button"
            onClick={onSchliessen}
            className="rounded-lg px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={onAusrollen}
            disabled={umstellenLaeuft}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            <Rocket size={13} />
            {haltend.length > 0 ? 'Trotzdem ausrollen' : 'Ausrollen'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Kasten({ schwere, children }: { schwere: 'haltend' | 'hinweis'; children: React.ReactNode }) {
  const haltend = schwere === 'haltend';
  return (
    <div
      className={`flex gap-2.5 rounded-lg border px-3 py-2.5 text-sm ${
        haltend
          ? 'border-red-200 bg-red-50 text-red-900 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-200'
          : 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200'
      }`}
    >
      <span className="mt-0.5 shrink-0">
        {haltend ? <AlertTriangle size={15} /> : <Info size={15} />}
      </span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

/**
 * Der Weg zum Anlegen einer Vorlage — ein Verweis, keine zweite Eingabemaske.
 *
 * Auf der Produktseite gibt es das Formular samt Vorabprüfung bereits. Es hier
 * zu wiederholen hiesse, zwei Masken für dieselbe Sache zu pflegen; die laufen
 * auseinander, und dann gilt die, die jemand zufällig zuerst findet.
 */
function ProduktLink({ produktId, text }: { produktId: string; text: string }) {
  return (
    <Link
      to={`/products/${produktId}`}
      className="mt-2 inline-flex items-center gap-1 text-sm font-medium underline underline-offset-2"
    >
      {text}
      <ExternalLink size={12} />
    </Link>
  );
}
